/* Add / rename a map — right side panel (same pattern as the Servers page panel).
 * Add: name, optional Steam Workshop id/link, optional image URL, live preview.
 * Edit: the API only renames (PUT /api/maps/:id { name }), so that's the only field. */
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import MapImage, { githubImage, prettyMapName } from './MapImage';

// Extract a workshop id from a full URL or bare id (mirrors the server parser).
export function parseWorkshopId(input) {
  const s = String(input || '').trim();
  if (!s) return null;
  if (/^\d{6,20}$/.test(s)) return s;
  const m = s.match(/[?&]id=(\d{6,20})/) || s.match(/sharedfiles\/filedetails\/(\d{6,20})/);
  return m ? m[1] : null;
}

export default function MapPanel({ open, editing, poolLabel, busy, onClose, onAdd, onRename }) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [image, setImage] = useState('');
  const [isWorkshop, setIsWorkshop] = useState(false);
  const [workshop, setWorkshop] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setName(editing ? editing.name : '');
    setImage(''); setIsWorkshop(false); setWorkshop(''); setError('');
  }, [open, editing]);

  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open, busy, onClose]);

  const clean = name.trim().toLowerCase();
  const wid = parseWorkshopId(workshop);
  const unchanged = editing && clean === editing.name;
  const canSave = !!clean && !busy && (editing ? !unchanged : (!isWorkshop || !!wid));

  // Preview: typed image URL → GitHub image for the name (stock maps) → existing image (edit)
  let preview = '';
  if (editing) preview = editing.workshop_id ? editing.image_url : (clean && !unchanged ? githubImage(clean) : editing.image_url);
  else preview = image.trim() || (clean && !isWorkshop ? githubImage(clean) : '');
  const previewWorkshop = editing ? !!editing.workshop_id : isWorkshop;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    setError('');
    const err = editing ? await onRename(editing, clean) : await onAdd({ name: clean, image_url: image.trim(), workshop_id: isWorkshop ? wid : undefined });
    if (err) setError(err);
  };

  return createPortal(
    <AnimatePresence>
      {open && (<>
        <motion.div className="mp-backdrop" onClick={() => { if (!busy) onClose(); }}
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }} />
        <motion.aside className="mp-panel" role="dialog" aria-modal="true"
          initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'tween', duration: 0.24, ease: [0.16, 1, 0.3, 1] }}>
          <form onSubmit={submit} className="mp-panel-form">
            <div className="mp-panel-head">
              <div>
                <h3>{editing ? t('mapsPage.renameTitle', 'Rename map') : t('mapsPage.addTitle', 'Add map')}</h3>
                <p>{t('mapsPage.toPool', 'Pool: {{pool}}', { pool: poolLabel })}</p>
              </div>
              <button type="button" className="mp-x" onClick={onClose} disabled={busy} aria-label={t('common.close', 'Close')}>×</button>
            </div>

            <div className="mp-panel-body">
              <div className="mp-preview">
                {preview ? <MapImage src={preview} workshop={previewWorkshop} /> : <div className="mp-preview-empty">{t('mapsPage.previewEmpty', 'Type a map name to see its image')}</div>}
                {clean && <div className="mp-preview-name"><b>{prettyMapName(clean)}</b><span>{clean}</span></div>}
              </div>

              <div className="mp-field">
                <label htmlFor="mp-name">{editing ? t('mapsPage.newName', 'New map name') : t('mapsPage.mapName', 'Map name')} <span className="req">*</span></label>
                <input id="mp-name" className="mp-input mono" placeholder="de_cache" value={name} onChange={e => setName(e.target.value)} autoFocus autoComplete="off" spellCheck={false} />
                <span className="hint">{t('mapsPage.nameHint', 'Must match the map\'s file name on the server exactly.')}</span>
              </div>

              {editing ? (
                <div className="mp-info">
                  {editing.workshop_id
                    ? <>{t('mapsPage.workshopMap', 'Workshop map')}, ID <code>{editing.workshop_id}</code> - {t('mapsPage.renameWorkshopHint', 'its image stays the same.')}</>
                    : t('mapsPage.renameStockHint', 'The image updates to match the new name.')}
                </div>
              ) : (<>
                <label className="mp-switch">
                  <input type="checkbox" checked={isWorkshop} onChange={e => setIsWorkshop(e.target.checked)} />
                  <span className="mp-switch-ui" />
                  <span>{t('mapsPage.workshopMap', 'Workshop map')}</span>
                </label>
                {isWorkshop && (
                  <div className="mp-field">
                    <label htmlFor="mp-ws">{t('mapsPage.workshopId', 'Workshop ID or link')} <span className="req">*</span></label>
                    <input id="mp-ws" className="mp-input mono" placeholder="3447707473" value={workshop} onChange={e => setWorkshop(e.target.value)} autoComplete="off" spellCheck={false} />
                    <span className={`hint${wid ? ' ok' : ''}`}>
                      {wid ? t('mapsPage.workshopParsed', 'Workshop ID: {{id}}', { id: wid })
                        : <>{t('mapsPage.workshopHint', 'Paste the Steam Workshop link or just the number after')} <code>?id=</code></>}
                    </span>
                  </div>
                )}
                <div className="mp-field">
                  <label htmlFor="mp-img">{t('mapsPage.imageUrl', 'Image URL')} <span className="opt">{t('mapsPage.optional', 'optional')}</span></label>
                  <input id="mp-img" className="mp-input" type="url" placeholder="https://…" value={image} onChange={e => setImage(e.target.value)} autoComplete="off" />
                  <span className="hint">{isWorkshop ? t('mapsPage.imageHintWs', 'Workshop maps have no automatic image.') : t('mapsPage.imageHint', 'Leave empty to use the image from GitHub.')}</span>
                </div>
              </>)}

              {error && <div className="mp-error">{error}</div>}
            </div>

            <div className="mp-panel-foot">
              <button type="button" className="mp-btn" onClick={onClose} disabled={busy}>{t('common.cancel', 'Cancel')}</button>
              <button type="submit" className="mp-btn primary" disabled={!canSave}>
                {busy ? t('mapsPage.saving', 'Saving…') : editing ? t('mapsPage.saveName', 'Save name') : t('mapsPage.addMap', 'Add map')}
              </button>
            </div>
          </form>
        </motion.aside>
      </>)}
    </AnimatePresence>,
    document.body
  );
}
